import * as React from 'react';
import styled from 'styled-components';
import { v4 as uuid } from 'uuid';

import { useShowable } from '../../../core/hooks';
import { fadeTop, fadeBottom, fadeLeft, fadeRight } from '../../Animation';
import { observeEffect } from '../../utils';
import { Layout, Entry, slotLayoutMaker } from '../slots';
import { 
    getAnimation as getAnimation_, getPosition, getSize, getTooltip as getTooltip_, 
    TooltipAgregator, AnimationsAgregator,
} from '..';
import { Context } from '../../Helpers';


const Tooltip_ = styled.div`
    background-color: #181839;
    box-shadow: 0px 7px 13px rgba(0, 0, 0, 0.2);
    border-radius: 4px;
    color: #fff;
    font-size: 14px;
    position: absolute;
    padding: 12px 15px 14px;
    transition: all .33s ease-in-out;

    &:before {
        content: '';
        width: 12px;
        height: 12px;
        border-radius: 4px;
        background-color: #181839;
        position: absolute;
    }
`;


const N = 9;


export const Top = styled(Tooltip_)`
    bottom: calc(100% + ${N}px);
    transform: translateX(-50%);

    &:before {
        left: 50%;
        top: calc(100% - ${N}px);
        transform: translateX(-50%) rotate(45deg);
    }
`;

export const Bottom = styled(Tooltip_)`
    top: calc(100% + ${N}px);
    transform: translateX(-50%);

    &:before {
        left: 50%;
        bottom: calc(100% - ${N}px);
        transform: translateX(-50%) rotate(45deg);
    }
`;

export const Left = styled(Tooltip_)`
    top: 50%;
    right: ${N}px;
    transform: translateY(-50%);

    &:before {
        top: 50%;
        right: ${N}px;
        transform: translate(100%, -50%) rotate(45deg);
    }
`;

export const Right = styled(Tooltip_)`
    top: 50%;
    left: ${N}px;
    transform: translateY(-50%);

    &:before {
        top: 50%;
        left: ${N}px;
        transform: translate(-100%, -50%) rotate(45deg);
    }
`;



const tooltips: TooltipAgregator = {
    top: Top,
    left: Left,
    right: Right,
    bottom: Bottom,
}

const animations: AnimationsAgregator = {
    top: fadeTop,
    left: fadeLeft,
    right: fadeRight,
    bottom: fadeBottom,
}

export const useControl = () => {
    const { isShowed, show, hide } = useShowable();

    return { 
        isShowed, 
        handlers: {
            onMouseEnter: show,
            onMouseLeave: hide,
        },
    };
};



interface ITooltipProps {

    show: boolean,

    /**
     * С какой стороны появиться
     */
    dir?: 'top' | 'bottom' | 'left' | 'right',

    className?: string,
}

export const makeTooltip = (tooltips: TooltipAgregator, animations: AnimationsAgregator): React.FunctionComponent<ITooltipProps> => {

    const getTooltip = getTooltip_(tooltips);
    const getAnimation = getAnimation_(animations);

    return ({ 
        children,
        show,
        className,
        dir = 'top',
    }) => {
        
        const targetRef = React.useRef<HTMLDivElement>(null);
        const [hover, setHover] = React.useState(show);

        const intersectParent = React.useMemo(() => {
            return new IntersectionObserver((entries) => {
                if(entries[0].intersectionRatio === 0){
                    setHover(false);
                }
            })
        }, []);


        React.useEffect(() => observeEffect(targetRef, intersectParent)(), []);
        React.useEffect(() => setHover(show), [show]);

        const id = React.useMemo(() => uuid(), []);


        const [pos, resetable] = getPosition(dir);
        const size = getSize(dir);
        const animate = getAnimation(dir);
        const Tooltip = getTooltip(dir);
    
    
        return (
            <Entry connectLayout={children}>
                <Layout 
                    name="target"
                    innerRef={targetRef}
                />
                <Context
                    id={id}
                    innerRef={targetRef}
                    position={pos}
                    size={size}
                    isShow={hover}
                    animation={animate}
                    resetable={resetable}>
                    <Tooltip className={className}>
                        <Layout name="text" />
                    </Tooltip>
                </Context>
            </Entry>
        );
    }
}


export const Tooltip = makeTooltip(tooltips, animations);

export const Target = slotLayoutMaker('target')(({ innerRef, children, className, ...props }) => {
    return (
        <Wrapper
            ref={innerRef}
            className={className}
            {...props}>
            {children}
        </Wrapper>
    );
});

const Wrapper = styled.span`
    display: inline-block;
`;

export const Text = slotLayoutMaker('text')(({ children }) => {
    return (
        <React.Fragment>
            {children}
        </React.Fragment>
    );
});
